import { z } from "zod";
import { DateTypeSchema } from "./core.schema";

// Schemas

export const FieldValidationTypeSchema = z.enum([
  "required",
  "isEmail",
  "minLength",
  "isTrue",
]);

export const FieldTypeSchema = z.enum([
  "text",
  "text-area",
  "radio-group",
  "select",
  "optional-select",
  "checkbox",
  "date-picker",
  "password",
  "approver",
  "presenter",
  "multiple-checkboxes",
]);

export const FieldLayoutConditionOperatorSchema = z.enum([
  "&&",
  "||",
  "!",
  "==",
  "!=",
  ">",
  "<",
  ">=",
  "<=",
]);

export const FieldValidationSchema = z.object({
  type: FieldValidationTypeSchema,
  value: z.union([z.string(), z.number(), z.boolean()]).optional(),
  message: z.string(),
});

export const FieldOptionSchema = z.object({
  value: z.union([z.string(), z.number()]),
  label: z.string(),
});

export const FieldPropsSchema = z.object({
  name: z.string(),
  placeholder: z.string().optional(),
  label: z.string().optional(),
  type: FieldTypeSchema,
  options: FieldOptionSchema.array().optional(),
  required: z.union([z.boolean(), z.string()]).optional(),
});

export const FieldLayoutConditionValueSchema = z.union([
  z.object({
    type: z.literal("constant"),
    value: z.union([z.boolean(), z.string(), z.number(), DateTypeSchema]),
  }),
  z.object({
    type: z.literal("field"),
    field: z.string(),
  }),
]);

const BaseFieldLayoutConditionSchema = z.object({
  type: z.literal("condition"),
  operator: FieldLayoutConditionOperatorSchema,
});

type BaseFieldLayoutCondition = z.infer<
  typeof BaseFieldLayoutConditionSchema
> & {
  operands: (
    | z.infer<typeof FieldLayoutConditionValueSchema>
    | BaseFieldLayoutCondition
  )[];
};

export const FieldLayoutConditionSchema: z.ZodType<BaseFieldLayoutCondition> =
  BaseFieldLayoutConditionSchema.extend({
    operands: z.lazy(() =>
      z
        .union([FieldLayoutConditionValueSchema, FieldLayoutConditionSchema])
        .array()
    ),
  });

export const BaseFieldLayoutSchema = z.object({
  sectionTitle: z.string().optional(),
  collapsible: z.boolean().optional(),
  defaultCollapsed: z.boolean().optional(),
  condition: FieldLayoutConditionSchema.optional(),
});

type BaseFieldLayout = z.infer<typeof BaseFieldLayoutSchema> & {
  children: (string | BaseFieldLayout)[];
};

export const FieldLayoutSchema: z.ZodType<BaseFieldLayout> =
  BaseFieldLayoutSchema.extend({
    children: z.lazy(() => z.union([z.string(), FieldLayoutSchema]).array()),
  });
